import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface podcastGenState {
  prompt: string;
  duration: number;
  generate_text: boolean;
  hosts: {
    character_name: string;
    voice_name: string;
    role: 'host' | 'guest';
    gender: 'M' | 'F';
    qualities: string[];
    age: number;
  }[];
  guests: {
    character_name: string;
    voice_name: string;
    role: 'guest' | 'host';
    gender: 'M' | 'F';
    qualities: string[];
    age: number;
  }[];
}

const initialState: podcastGenState = {
  prompt: '',
  duration: 0,
  generate_text: true,
  hosts: [
    {
      character_name: '',
      role: 'host',
      gender: 'M',
      age: 0,
      voice_name: '',
      qualities: [],
    },
  ],
  guests: [
    {
      character_name: '',
      role: 'guest',
      gender: 'M',
      age: 0,
      voice_name: '',
      qualities: [],
    },
  ],
};

const podcastGenSlice = createSlice({
  name: 'podcastGenSlice',
  initialState,
  reducers: {
    setPrompt: (state, action: PayloadAction<{ prompt: string }>) => {
      state.prompt = action.payload.prompt;
    },
    setDuration: (state, action: PayloadAction<{ duration: number }>) => {
      state.duration = action.payload.duration;
    },
    setCharacterVoice: (
      state,
      action: PayloadAction<{
        index: number;
        voice_name: string;
        role: 'host' | 'guest';
        gender: 'M' | 'F';
      }>
    ) => {
      const { index, voice_name, gender } = action.payload;

      // Ensure the array has the required index
      if (action.payload.role === 'host') {
        if (index >= 0 && index < state.hosts.length) {
          state.hosts[index].voice_name = voice_name;
          state.hosts[index].gender = gender;
        }
      } else {
        if (index >= 0 && index < state.guests.length) {
          state.guests[index].voice_name = voice_name;
          state.guests[index].gender = gender;
        }
      }
    },
    addCharacter: (
      state,
      action: PayloadAction<{
        character_name: string;
        role: 'host' | 'guest';
        gender: 'M' | 'F';
        age: number;
      }>
    ) => {
      const newCharacter = {
        ...action.payload,
        voice_name: '',
        qualities: [],
      };
      if (action.payload.role === 'host') {
        state.hosts.push(newCharacter);
      } else if (action.payload.role === 'guest') {
        state.guests.push(newCharacter);
      }
    },
    removeCharacter: (
      state,
      action: PayloadAction<{ role: 'host' | 'guest'; index: number }>
    ) => {
      const { index, role } = action.payload;
      const targetArray = role === 'guest' ? state.guests : state.hosts;
      if (index >= 0 && index < targetArray.length) {
        targetArray.splice(index, 1); // Remove the character at the specified index
      }
    },
    addCharacterDetails: (
      state,
      action: PayloadAction<{
        index: number;
        role: 'host' | 'guest';
        character_name?: string;
        age?: number;
        qualities?: string[];
      }>
    ) => {
      const { index, role, character_name, age, qualities } = action.payload;
      const target = role === 'host' ? state.hosts : state.guests;
      if (target[index]) {
        if (character_name !== undefined)
          target[index].character_name = character_name;
        if (age !== undefined) target[index].age = age;
        if (qualities !== undefined) target[index].qualities = qualities;
      }
    },
    discardFormData: (state) => {
      Object.assign(state, initialState);
    },
  },
});

export const {
  setPrompt,
  setDuration,
  setCharacterVoice,
  addCharacter,
  removeCharacter,
  addCharacterDetails,
  discardFormData,
} = podcastGenSlice.actions;
export default podcastGenSlice.reducer;
